import { useEffect, useState } from "react";
import { apiFetch } from "../lib/api";

interface DashboardData {
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
    avatarUrl?: string | null;
  };
  company: {
    id: string;
    name: string;
    plan: string;
    licenseStatus: string;
    deploymentStatus: string;
  } | null;
  stats: {
    leads: number;
    tickets: number;
    license: {
      id: string;
      plan: string;
      status: string;
      maxUsers: number;
      currentUsers: number;
    } | null;
    deployment: {
      id: string;
      status: string;
      currentStage: string;
    } | null;
  } | null;
}

const statusLabels: Record<string, string> = {
  pending: "Pendente",
  documents_pending: "Documentos Pendentes",
  scheduled: "Agendado",
  in_progress: "Em Andamento",
  completed: "Concluído",
  evaluation: "Avaliação",
  active: "Ativa",
  expired: "Expirada",
  suspended: "Suspensa",
};

export default function DashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.href = "/login";
      return;
    }

    apiFetch("/api/dashboard")
      .then((json) => {
        if (json.success) {
          setData(json.data);
        } else {
          window.location.href = "/login";
        }
      })
      .catch(() => {
        window.location.href = "/login";
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent-600 border-t-transparent" />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-600 text-white font-bold">
              Z
            </div>
            <span className="text-xl font-bold">ZWeb</span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="/perfil"
              className="flex items-center gap-3 rounded-full py-1 pl-1 pr-2 text-sm text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-900 dark:hover:text-white"
            >
              <span className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-accent-100 font-semibold text-accent-700 dark:border-gray-700 dark:bg-accent-900/60 dark:text-accent-200">
                {data.user.avatarUrl ? (
                  <img
                    src={data.user.avatarUrl}
                    alt={`Foto de perfil de ${data.user.name}`}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  data.user.name.charAt(0).toUpperCase()
                )}
              </span>
              <span>{data.user.name}</span>
            </a>
            <button
              onClick={() => {
                localStorage.removeItem("token");
                window.location.href = "/";
              }}
              className="text-sm text-red-600 hover:underline"
            >
              Sair
            </button>
          </div>
        </div>
      </header>

      <main className="container py-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Dashboard</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Bem-vindo à sua plataforma comercial.
            </p>
          </div>
        </div>

        {/* Status Cards */}
        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-950">
            <div className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Leads
            </div>
            <div className="mt-2 text-3xl font-bold">{data.stats?.leads || 0}</div>
            <div className="mt-1 text-sm text-green-600">+0% este mês</div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-950">
            <div className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Chamados
            </div>
            <div className="mt-2 text-3xl font-bold">{data.stats?.tickets || 0}</div>
            <div className="mt-1 text-sm text-green-600">+0% este mês</div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-950">
            <div className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Plano
            </div>
            <div className="mt-2 text-3xl font-bold capitalize">
              {data.company?.plan || "N/A"}
            </div>
            <div className="mt-1 text-sm text-gray-500">
              {data.company?.licenseStatus
                ? statusLabels[data.company.licenseStatus]
                : "Sem licença"}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-950">
            <div className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Implantação
            </div>
            <div className="mt-2 text-3xl font-bold">
              {data.company?.deploymentStatus
                ? statusLabels[data.company.deploymentStatus]
                : "N/A"}
            </div>
            <div className="mt-1 text-sm text-gray-500">
              {data.stats?.deployment
                ? `Etapa: ${data.stats.deployment.currentStage}`
                : "Não iniciada"}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <a
            href="/chamados"
            className="rounded-2xl border border-gray-200 bg-white p-6 transition-shadow hover:shadow-md dark:border-gray-800 dark:bg-gray-950"
          >
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-100 text-accent-600 dark:bg-accent-900 dark:text-accent-300">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <div>
                <div className="font-semibold">Chamados</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Abrir novo chamado
                </div>
              </div>
            </div>
          </a>

          <a
            href="/downloads"
            className="rounded-2xl border border-gray-200 bg-white p-6 transition-shadow hover:shadow-md dark:border-gray-800 dark:bg-gray-950"
          >
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-300">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </div>
              <div>
                <div className="font-semibold">Downloads</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Acessar materiais
                </div>
              </div>
            </div>
          </a>

          <a
            href="/licencas"
            className="rounded-2xl border border-gray-200 bg-white p-6 transition-shadow hover:shadow-md dark:border-gray-800 dark:bg-gray-950"
          >
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-100 text-accent-600 dark:bg-accent-900 dark:text-accent-300">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
              <div>
                <div className="font-semibold">Licenças</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Gerenciar licenças
                </div>
              </div>
            </div>
          </a>

          <a
            href="/cliente/implantacao"
            className="rounded-2xl border border-gray-200 bg-white p-6 transition-shadow hover:shadow-md dark:border-gray-800 dark:bg-gray-950"
          >
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-300">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <div>
                <div className="font-semibold">Implantação</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Acompanhar status
                </div>
              </div>
            </div>
          </a>
        </div>

        {/* Recent Activity */}
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-950">
            <h2 className="text-lg font-semibold">Próximos Compromissos</h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Nenhum compromisso agendado.
            </p>
            <a
              href="/cronograma"
              className="mt-4 inline-block text-sm text-accent-600 hover:underline"
            >
              Ver cronograma
            </a>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-950">
            <h2 className="text-lg font-semibold">Treinamentos</h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Nenhum treinamento em andamento.
            </p>
            <a
              href="/treinamentos"
              className="mt-4 inline-block text-sm text-accent-600 hover:underline"
            >
              Ver treinamentos
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}
